import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Snackbar,
  Alert,
  Chip,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
} from "@mui/material";
import { styled } from '@mui/material/styles';

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const faqs = [
  {
    id: 1,
    question: "What is Peer2Learn?",
    answer:
      "Peer2Learn is an innovative online learning platform that connects students (ages 8 and up) with qualified peer tutors for personalized academic support. We offer a wide range of subjects and allow learners to choose tutors who match their learning style, schedule, and budget.",
    category: "General",
  },
  {
    id: 2,
    question: "Who can use Peer2Learn?",
    answer:
      "Anyone aged 8 and above can use Peer2Learn. Students under 18 must have parental or guardian consent to register and participate in tutoring sessions. We have specific safety measures and monitoring systems in place for younger learners.",
    category: "General",
  },
  {
    id: 3,
    question: "Is Peer2Learn only for school students?",
    answer:
      "Not at all! While many of our learners are school or college students, we welcome anyone seeking academic support or peer tutoring. This includes adult learners, professionals looking to upskill, and anyone passionate about continuous learning.",
    category: "General",
  },
  {
    id: 4,
    question: "How do I book a tutoring session?",
    answer:
      "Booking a session is simple: Sign up for a free account, browse available tutors by subject or skill, view their profiles, ratings, and availability, then book and pay for a session that fits your schedule. You'll receive instant confirmation and meeting details.",
    category: "Students & Parents",
  },
  {
    id: 5,
    question: "Do parents need to supervise sessions for younger children?",
    answer:
      "Yes. Parents or guardians are responsible for supervising students under 18, especially those under 13. We recommend parents be present in the same room during sessions to ensure safety, engagement, and the best learning experience.",
    category: "Students & Parents",
  },
  {
    id: 6,
    question: "What subjects do you offer?",
    answer:
      "We offer a comprehensive range of subjects including Mathematics, Sciences (Physics, Chemistry, Biology), English, Languages, Coding, Test Preparation, and many more. New subjects are added regularly based on student demand and tutor expertise.",
    category: "Subjects & Tutoring",
  },
  {
    id: 7,
    question: "How are tutors vetted?",
    answer:
      "All tutors undergo a rigorous screening process including background checks, academic credential verification, interview assessments, and a probationary teaching period. Only the top 15% of applicants are accepted to ensure quality education.",
    category: "Safety & Quality",
  },
];

const locations = [
  {
    city: "Lagos (Headquarters)",
    address: "No 38 Opebi Road, Ikeja, Lagos, Nigeria",
    phone: "+234 816 455 7305",
    email: "lagos@peer2learn.com",
    hours: "Mon-Fri: 8am - 8pm, Sat: 9am - 5pm",
    coordinates: "6.6018,3.3515",
    mapEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.508570513449!2d3.3493!3d6.6018!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103b93b5e7b1e8b1%3A0x6b8f7e9c8e4b9a!2sOpebi%20Rd%2C%20Ikeja%2C%20Lagos!5e0!3m2!1sen!2sng!4v1635447890123!5m2!1sen!2sng"
  },
  {
    city: "Abuja",
    address: "Plot 104 Emmanuel Adiele Street, off Mike Akhigbe Way, Jabi, Abuja, Nigeria",
    phone: "+234 816 455 7306",
    email: "abuja@peer2learn.com",
    hours: "Mon-Fri: 8am - 6pm, Sat: 10am - 4pm",
    coordinates: "9.0579,7.4895",
    mapEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3940.287654321!2d7.4895!3d9.0579!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104e0b5b7e8b1e8b%3A0x6b8f7e9c8e4b9a!2sJabi%2C%20Abuja!5e0!3m2!1sen!2sng!4v1635447891234!5m2!1sen!2sng"
  }
];

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '24px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)',
  border: '1px solid rgba(25, 118, 210, 0.1)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(25, 118, 210, 0.08)',
    borderColor: 'rgba(25, 118, 210, 0.2)',
    transform: 'translateY(-2px)',
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: '16px !important',
  marginBottom: '12px',
  boxShadow: 'none',
  border: '1px solid rgba(25, 118, 210, 0.1)',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    margin: '0 0 12px 0',
    borderColor: '#1976D2',
  },
}));

const ContactChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(25, 118, 210, 0.04)',
  color: '#1976D2',
  fontWeight: 600,
  borderRadius: '30px',
  border: 'none',
}));

const MapOverlay = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  bottom: 16,
  left: 16,
  right: 16,
  padding: '20px',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(10px)',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(25, 118, 210, 0.08)',
  border: '1px solid rgba(25, 118, 210, 0.1)',
}));

const EmailLink = styled(Box)({
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: '#1976D2',
  },
});

const PhoneLink = styled(Box)({
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: '#1976D2',
  },
});

export default function Contact() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedLocation, setSelectedLocation] = useState(locations[0]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleEmailClick = (email) => {
    window.location.href = `mailto:${email}`;
    setSnackbar({
      open: true,
      message: 'Opening email client...',
      severity: 'success'
    });
  };

  const handlePhoneClick = (phone) => {
    window.location.href = `tel:${phone.replace(/\s/g, '')}`;
    setSnackbar({
      open: true,
      message: 'Initiating call...',
      severity: 'success'
    });
  };

  return (
    <Box sx={{ 
      pt: { xs: 10, md: 14 }, 
      pb: { xs: 6, md: 10 },
      backgroundColor: '#ffffff',
      minHeight: '100vh',
    }}>
      <Container maxWidth="lg">
        
        {/* Minimal Header */}
        <Box textAlign="center" mb={8}>
          <ContactChip 
            label="CONTACT US" 
            size="small"
            sx={{ mb: 2 }}
          />
          <Typography 
            variant="h2" 
            fontWeight={500} 
            gutterBottom
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              letterSpacing: '-0.02em',
              color: '#000000',
            }}
          >
            Get in touch
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(0, 0, 0, 0.6)',
              maxWidth: '500px', 
              mx: 'auto',
              fontSize: '1.1rem',
              lineHeight: 1.6,
            }}
          >
            We're here to help with any questions about tutoring, 
            technical support, or partnerships.
          </Typography>
        </Box>

        {/* Contact Cards - Fully Functional */}
        <Grid container spacing={3} mb={8}>
          {/* Email Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <StyledCard onClick={() => handleEmailClick('support@peer2learn.com')}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <EmailIcon sx={{ fontSize: 40, color: '#1976D2' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: '#000000' }}>
                  Email Us
                </Typography>
                <EmailLink>
                  <Typography variant="body2" sx={{ color: '#1976D2', mb: 2, fontWeight: 500 }}>
                    support@peer2learn.com
                  </Typography>
                </EmailLink>
                <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.4)' }}>
                  Click card to send email
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Phone Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <StyledCard onClick={() => handlePhoneClick('+2348164557305')}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <PhoneIcon sx={{ fontSize: 40, color: '#1976D2' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: '#000000' }}>
                  Call Us
                </Typography>
                <PhoneLink>
                  <Typography variant="body2" sx={{ color: '#1976D2', mb: 2, fontWeight: 500 }}>
                    +234 816 455 7305
                  </Typography>
                </PhoneLink>
                <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.4)' }}>
                  Click card to call
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Hours Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <StyledCard sx={{ cursor: 'default' }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <AccessTimeIcon sx={{ fontSize: 40, color: '#1976D2' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: '#000000' }}>
                  Support Hours
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)', mb: 1 }}>
                  Mon-Fri: 8am - 8pm
                </Typography>
                <Typography variant="body2" sx={{ color: '#1976D2', fontWeight: 500 }}>
                  Saturday: 9am - 5pm
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Map Section */}
        <Box sx={{ mb: 8 }}>
          <StyledCard sx={{ overflow: 'hidden', p: 0 }}>
            {/* Location Tabs */}
            <Box sx={{ 
              display: 'flex', 
              p: 2, 
              gap: 1,
              borderBottom: '1px solid rgba(25, 118, 210, 0.1)',
            }}>
              {locations.map((loc) => (
                <Button
                  key={loc.city}
                  onClick={() => setSelectedLocation(loc)}
                  sx={{ 
                    flex: isMobile ? 1 : 'auto',
                    color: selectedLocation.city === loc.city ? '#ffffff' : '#000000',
                    backgroundColor: selectedLocation.city === loc.city ? '#1976D2' : 'transparent',
                    borderRadius: '30px',
                    padding: '6px 20px',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    '&:hover': {
                      backgroundColor: selectedLocation.city === loc.city ? '#1976D2' : 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  {loc.city.split(' ')[0]}
                </Button>
              ))}
            </Box>

            {/* Map Container */}
            <Box sx={{ position: 'relative', height: '450px' }}>
              <iframe
                title={`Peer2Learn ${selectedLocation.city}`}
                src={selectedLocation.mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, display: 'block' }}
                loading="lazy"
                allowFullScreen
              />

              {/* Clean Overlay */}
              <MapOverlay>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <LocationOnIcon sx={{ color: '#1976D2', fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#000000' }}>
                    {selectedLocation.city}
                  </Typography>
                </Stack>
                
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)', mb: 2 }}>
                  {selectedLocation.address}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.5)', display: 'block', mb: 0.5 }}>
                      Phone
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#1976D2', 
                        fontWeight: 500,
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => handlePhoneClick(selectedLocation.phone)}
                    >
                      {selectedLocation.phone}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.5)', display: 'block', mb: 0.5 }}>
                      Hours
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#000000', fontWeight: 500 }}>
                      {selectedLocation.hours}
                    </Typography>
                  </Grid>
                </Grid>
              </MapOverlay>
            </Box>
          </StyledCard>
        </Box>

        {/* FAQ Section */}
        <Box mb={4}>
          <Typography 
            variant="h4" 
            fontWeight={500} 
            textAlign="center"
            sx={{ 
              mb: 1,
              color: '#000000',
              letterSpacing: '-0.02em',
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              color: 'rgba(0, 0, 0, 0.6)',
              mb: 6,
              maxWidth: '500px',
              mx: 'auto',
            }}
          >
            Quick answers to common questions about Peer2Learn
          </Typography>

          {faqs.map((faq) => (
            <StyledAccordion key={faq.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#1976D2' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={faq.category} 
                    size="small"
                    sx={{ 
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      color: '#1976D2',
                      borderRadius: '30px',
                      fontWeight: 500,
                    }}
                  />
                  <Typography fontWeight={500} sx={{ color: '#000000' }}>
                    {faq.question}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 2, borderColor: 'rgba(25, 118, 210, 0.1)' }} />
                <Typography sx={{ 
                  color: 'rgba(0, 0, 0, 0.7)', 
                  lineHeight: 1.8,
                  fontSize: '0.95rem',
                }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </StyledAccordion>
          ))}
        </Box>

        {/* Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.severity} 
            sx={{ 
              borderRadius: '30px',
              backgroundColor: '#ffffff',
              color: '#000000',
              border: '1px solid rgba(25, 118, 210, 0.2)',
              '& .MuiAlert-icon': { color: '#1976D2' }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}